import {Field, FormikProvider, useFormik} from 'formik';
import {useTranslation} from 'react-i18next';

import {Chip, Divider, FormControlLabel, FormGroup, FormHelperText, Grid, Switch} from '@mui/material';

import {CompanyProfileDialogButtons} from '../CompanyProfileDialogButtons';
import {PROFILE_SWITCH_SX} from '../profileDialogStyles';

import {EmpionTextField} from '../../../../../components/EmpionTextField/EmpionTextField';
import {CompanyProfile} from '../../types';

interface CompanyProfileHeaderFormProps {
  company: CompanyProfile;
  onCancel(): void;
  onSubmit(company: CompanyProfile): void;
}

export const CompanyProfileHeaderForm = ({company, onCancel, onSubmit}: CompanyProfileHeaderFormProps) => {
  const {t} = useTranslation();

  const handleCancel = () => {
    onCancel();
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: company?.name,
      slogan_de: company?.slogan?.de ?? '',
      slogan_en: company?.slogan?.en ?? '',
      industry_de: company?.industry?.de ?? '',
      industry_en: company?.industry?.en ?? '',
      toggle: !!company.industry?.en || !!company.slogan?.en,
      website_url: company?.websiteUrl ?? '',
      linkedin_url: company?.linkedinUrl ?? '',
      xing_url: company?.xingUrl ?? '',
    },
    validate: (values) => {
      const errors: Partial<any> = {};
      const urlPattern = /^https:\/\/([\w.-]+\.[a-zA-Z]{2,6})(\/[\w./?%&=~-]*)?(#[\w-]*)?$/;

      if (!values.name) {
        errors.name = t('shared.formValidation.required', {
          fieldName: t('companyProfile.header.dialog.formLabels.name'),
        });
      }
      if (values.website_url && !urlPattern.test(values.website_url)) {
        errors.website_url = 'shared.formValidation.incorrectFormat';
      }
      if (values.linkedin_url && !urlPattern.test(values.linkedin_url)) {
        errors.linkedin_url = 'shared.formValidation.incorrectFormat';
      }
      if (values.xing_url && !urlPattern.test(values.xing_url)) {
        errors.xing_url = 'shared.formValidation.incorrectFormat';
      }
      return errors;
    },
    onSubmit: async (values) => {
      const updatedData: Partial<CompanyProfile> = {
        resourceId: company.resourceId,
        name: values.name,
        slogan: {de: values.slogan_de, en: values.slogan_en},
        industry: {de: values.industry_de, en: values.industry_en},
        websiteUrl: values.website_url,
        linkedinUrl: values.linkedin_url,
        xingUrl: values.xing_url,
      };

      onSubmit({...company, ...updatedData});
    },
  });

  return (
    <FormikProvider value={formik}>
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={2} justifyContent={'space-between'}>
          <Grid item xs={12} container justifyContent={'flex-end'}>
            <FormGroup>
              <FormControlLabel
                name='toggle'
                control={<Switch checked={formik.values.toggle} sx={PROFILE_SWITCH_SX} />}
                label={t('shared.switchLabel')}
                onChange={formik.handleChange}
              />
            </FormGroup>
          </Grid>

          <Grid item container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Field
                name='name'
                label={t('companyProfile.header.dialog.formLabels.name')}
                component={EmpionTextField}
                fullWidth
                id='name'
              />
              {formik.errors.name && <FormHelperText>{t(formik.errors.name)}</FormHelperText>}
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field
                name='industry_de'
                label={t('companyProfile.header.dialog.formLabels.industry')}
                component={EmpionTextField}
                fullWidth
                id='industry_de'
              />
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Field
              name='slogan_de'
              label={t('companyProfile.header.dialog.formLabels.slogan')}
              component={EmpionTextField}
              fullWidth
              id='slogan_de'
            />
          </Grid>

          <Grid item container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Field
                name='website_url'
                label={t('companyProfile.header.dialog.formLabels.websiteUrl')}
                component={EmpionTextField}
                fullWidth
                id='website_url'
              />
              {formik.errors.website_url && <FormHelperText>{t(formik.errors.website_url)}</FormHelperText>}
            </Grid>
            <Grid item xs={12} sm={4}>
              <Field
                name='linkedin_url'
                label={t('companyProfile.header.dialog.formLabels.linkedinUrl')}
                component={EmpionTextField}
                fullWidth
                id='linkedin_url'
              />
              {formik.errors.linkedin_url && <FormHelperText>{t(formik.errors.linkedin_url)}</FormHelperText>}
            </Grid>
            <Grid item xs={12} sm={4}>
              <Field
                name='xing_url'
                label={t('companyProfile.header.dialog.formLabels.xingUrl')}
                component={EmpionTextField}
                fullWidth
                id='xing_url'
              />
              {formik.errors.xing_url && <FormHelperText>{t(formik.errors.xing_url)}</FormHelperText>}
            </Grid>

            {/* English input fields */}
            {formik.values.toggle && (
              <>
                <Divider
                  style={{
                    width: '100%',
                    padding: '32px 0 0 16px',
                    alignItems: 'flex-start',
                  }}
                >
                  <Chip label={t('shared.chipLabel')} variant='outlined' />
                </Divider>
                <Grid container spacing={2} mt={2} pl={2}>
                  <Grid item xs={6}>
                    <Field
                      name='industry_en'
                      label={t('companyProfile.header.dialog.formLabels.industry')}
                      component={EmpionTextField}
                      fullWidth
                      id='industry_en'
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Field
                      name='slogan_en'
                      label={t('companyProfile.header.dialog.formLabels.slogan')}
                      component={EmpionTextField}
                      fullWidth
                      id='slogan_en'
                    />
                  </Grid>
                </Grid>
              </>
            )}
          </Grid>

          {/* Buttons */}
          <Grid item container justifyContent={'flex-end'} display={'flex'}>
            <CompanyProfileDialogButtons onCancel={handleCancel} />
          </Grid>
        </Grid>
      </form>
    </FormikProvider>
  );
};
